import {
  buildFetchOptionsWithAuth,
  fetchJson,
  showApiError,
  useMutation,
  useQueryClient,
} from '@fv/client-core'
import type { ListShipment } from '@fv/client-types'

import { apiUri } from '@/constants'
import { useUpdateCachedLoad } from '@/hooks/shipments/useUpdateCachedLoad'
import type { ApiQuoteRequestDTO } from '@/types'

import { inProcessShipmentKeys } from '../shipment-list/hooks/useInProcessShipments'
import { quoteRequestKeys } from './useQuoteRequest'

async function postQuoteRequest(
  quoteRequest: ApiQuoteRequestDTO,
): Promise<ListShipment[]> {
  const endpoint = `${apiUri}/shipments/quote-request`
  const options = buildFetchOptionsWithAuth({
    body: JSON.stringify(quoteRequest),
    method: 'POST',
  })

  const response = await fetchJson(endpoint, options)
  if (response.ok) return response.json
  throw response.errorMessage
}

export function useCreateQuoteRequest() {
  const queryClient = useQueryClient()
  const updateCachedLoad = useUpdateCachedLoad()

  const mutation = useMutation(postQuoteRequest, {
    onSettled: (loads, error) => {
      if (error) {
        showApiError('Unable to create quote request', error, {
          'locations.0': 'Origin',
          'locations.1': 'Destination',
        })
        mutation.reset()
        return
      }

      if (loads) {
        queryClient.resetQueries({
          exact: true,
          queryKey: ['recent-shipment', loads[0].workflow],
        })
      }
      const quoteRequestId = loads?.[0]?.quoteRequestId

      loads?.forEach(updatedLoad => {
        updateCachedLoad({
          keysToSkip: ['shipments', 'quote-request'],
          updatedLoad,
        })
      })

      if (quoteRequestId) {
        queryClient.invalidateQueries({
          queryKey: quoteRequestKeys.request(quoteRequestId),
        })
      }

      queryClient.invalidateQueries({ queryKey: inProcessShipmentKeys.all })
    },
  })

  return mutation
}
